import React, { createContext, useContext, useState, useCallback } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import "./App.css";
import LoginPage from "./components/LoginPage";
import SignupPage from "./components/SignupPage";
import ProfilePage from "./components/ProfilePage";
import MlmTree from "./components/MlmTree";
import MemberSubtree from "./components/MemberSubtree";
import TransactionPage from "./components/TransactionPage";
import { authenticate, registerUser } from "./script/rpc";

/* ═══════════════════════════════════════════════════════
   Auth Context — stores uid + password, persists to
   sessionStorage so a page refresh keeps you logged in.
   ═══════════════════════════════════════════════════════ */
const AuthContext = createContext(null);

function loadSession() {
  try {
    const raw = sessionStorage.getItem("mlm_auth");
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(() => loadSession());

  const login = useCallback((uid, password) => {
    const data = { uid, password };
    setAuth(data);
    sessionStorage.setItem("mlm_auth", JSON.stringify(data));
  }, []);

  const logout = useCallback(() => {
    setAuth(null);
    sessionStorage.removeItem("mlm_auth");
  }, []);

  return (
    <AuthContext.Provider value={{ auth, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }

/* ═══════════════════════════════════════════════════════
   Protected Route — redirects to /login if not authed
   ═══════════════════════════════════════════════════════ */
function ProtectedRoute({ children }) {
  const { auth } = useAuth();
  return auth ? children : <Navigate to="/login" replace />;
}

/* ═══════════════════════════════════════════════════════
   Login wrapper — passes router navigate into LoginPage
   ═══════════════════════════════════════════════════════ */
function LoginRoute() {
  const { auth, login } = useAuth();
  const navigate = useNavigate();

  if (auth) return <Navigate to="/tree" replace />;

  return (
    <LoginPage
      onLogin={(uid, password) => {
        login(uid, password);
        navigate("/tree", { replace: true });
      }}
    />
  );
}

/* ═══════════════════════════════════════════════════════
   Signup wrapper
   ═══════════════════════════════════════════════════════ */
function SignupRoute() {
  const { auth, login } = useAuth();
  const navigate = useNavigate();
  const { search } = useLocation();

  if (auth) return <Navigate to={`/profile${search}`} replace />;

  const handleSignup = async (name, username, password) => {
    // 1. Create the user directly in Odoo using the admin proxy script!
    try {
      await registerUser(name, username, password);
    } catch (err) {
      throw new Error("Failed to create user in backend: " + err.message);
    }
    
    // 2. Immediately log in as the newly created real user
    try {
      const uid = await authenticate(username, password);
      login(uid, password);
      navigate(`/profile${search}`, { replace: true });
    } catch (err) {
      throw new Error("User created, but login failed: " + err.message);
    }
  };

  return <SignupPage onSignup={handleSignup} />;
}

/* ═══════════════════════════════════════════════════════
   Profile wrapper — pulls auth from context
   ═══════════════════════════════════════════════════════ */
function ProfileRoute() {
  const { auth } = useAuth();
  return <ProfilePage uid={auth.uid} password={auth.password} />;
}

/* ═══════════════════════════════════════════════════════
   Tree wrapper — pulls auth from context
   ═══════════════════════════════════════════════════════ */
function TreeRoute() {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = useCallback(() => {
    logout();
    navigate("/login", { replace: true });
  }, [logout, navigate]);

  return (
    <MlmTree
      uid={auth.uid}
      password={auth.password}
      onLogout={handleLogout}
    />
  );
}

/* ═══════════════════════════════════════════════════════
   Transaction wrapper
   ═══════════════════════════════════════════════════════ */
function TransactionRoute() {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <TransactionPage
      uid={auth.uid}
      password={auth.password}
      onLogout={() => {
        logout();
        navigate("/login", { replace: true });
      }}
    />
  );
}

/* ═══════════════════════════════════════════════════════
   Root App
   ═══════════════════════════════════════════════════════ */
export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Default → /login */}
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* Public */}
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/signup" element={<SignupRoute />} />

          {/* Protected */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfileRoute />
              </ProtectedRoute>
            }
          />

          <Route
            path="/tree"
            element={
              <ProtectedRoute>
                <TreeRoute />
              </ProtectedRoute>
            }
          />

          <Route
            path="/transactions"
            element={
              <ProtectedRoute>
                <TransactionRoute />
              </ProtectedRoute>
            }
          />

          <Route
            path="/tree/:memberId"
            element={
              <ProtectedRoute>
                <MemberSubtree />
              </ProtectedRoute>
            }
          />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}